import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Circle, Loader2, PhoneOff, X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { hangupAiCall, hangupError, type HangupError, type LiveCall } from "../../../lib/aiLive";
import { dispositionKey, dispositionTone } from "../../../lib/aiOutboundResults";
import { formatPhone } from "../../../lib/phone";
import { StopAllButton, StopNote, type StopResult } from "../../sections/ai-calls/automation/AutomationControls";
import { Note } from "../../sections/settings/panel/fields";
import { Btn, IconBtn, StatusPill, fmtDuration } from "../../ui";
import { useElapsed } from "../useElapsed";
import { AiCallDialing } from "./AiCallDialing";
import { AiCallTranscript } from "./AiCallTranscript";
import { AiRecordingStop } from "./AiRecordingStop";
import { AiTakeoverButton } from "./AiTakeoverButton";
import { takeoverBusy, takeoverState, takenOver } from "./takeoverView";
import { browserTime, dockCalls, endReason, type AiLiveState } from "./aiLive";
import { useAiLiveCalls } from "./useAiLiveCalls";

/**
 * The AI call dock (ring R5): while the AI is on the phone, any operator with
 * the CRM open sees the call as if they were dialling it themselves. The number
 * appears, it rings, the clock runs, and the conversation arrives line by line.
 *
 * A card in the bottom-left corner, apart from the operator's own call window
 * and its pill in the bottom-right, and apart from `CallProvider`'s state on
 * purpose: watching an AI call must never make the operator's own phone look
 * busy. The one way in is "take over in this browser" (ring R6d), which opens
 * that call window on the AI's call - and only when somebody presses it.
 *
 * What it shows follows the server's access lines: the conversation only for a
 * call the operator may hear, otherwise the line the results page uses. "End
 * call" is for everybody who sees the call; "Stop all" for managers, the same
 * button and confirmation as on the automation tab. An ended call stays for 20
 * seconds with how it ended, then leaves by itself; any card can be closed.
 *
 * The call's own part is keyed by the call, so a new call starts expanded and
 * with no leftover error. "Stop all" is not: its confirmation asks about every
 * call, so it stays open when the call on the card ends or another one takes
 * its place, and the card it was pressed on stays while it asks.
 */
export function AiCallDock({ canManage }: { canManage: boolean }) {
  const t = useT();
  const { state, dispatch, now } = useAiLiveCalls(true);
  const [stopResult, setStopResult] = useState<StopResult | null>(null);
  const [heldId, setHeldId] = useState<string | null>(null);
  const calls = dockCalls(state, now);
  const call = calls[0] ?? (heldId ? state.calls[heldId] : undefined);
  if (!call) return null;
  const ended = call.phase === "ended";

  return (
    <section className={cn("crm-ailive", `is-${call.phase}`)} aria-label={t("aiCalls.live.region")}>
      <AiCallCard
        key={call.id}
        call={call}
        others={calls.filter((c) => c.id !== call.id).length}
        state={state}
        onDismiss={() => dispatch({ type: "dismiss", id: call.id })}
      />
      <footer className="crm-ailive-foot">
        {ended ? (
          <Link to={`/calls/${call.id}`} className="crm-btn crm-btn-sm">
            {t("aiCalls.live.openCall")}
          </Link>
        ) : takeoverBusy(call) ? (
          // The AI is about to stop talking and an operator is on their way in:
          // "end call" pressed now would hang up on both of them.
          null
        ) : takenOver(call) ? (
          // Two people are talking and the AI has left. "End call" was the AI's
          // stop button and the server refuses it now (`taken_over`); what is
          // left to press is the recording of their conversation (ring R6c).
          <AiRecordingStop
            key={`r-${call.id}`}
            callId={call.id}
            canStop={call.canStopRecording}
            onStopped={() => dispatch({ type: "recordingStopped", id: call.id })}
          />
        ) : (
          <>
            {call.canTakeOver ? <AiTakeoverButton key={`t-${call.id}`} call={call} /> : null}
            <HangupButton key={call.id} callId={call.id} canManage={canManage} />
          </>
        )}
        {canManage ? (
          <StopAllButton quiet onStopped={setStopResult} onAsking={(asking) => setHeldId(asking ? call.id : null)} />
        ) : null}
      </footer>
      {stopResult ? <StopNote result={stopResult} onClose={() => setStopResult(null)} /> : null}
    </section>
  );
}

function AiCallCard({
  call,
  others,
  state,
  onDismiss,
}: {
  call: LiveCall;
  others: number;
  state: AiLiveState;
  onDismiss: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(true);
  const ended = call.phase === "ended";
  const answeredAt = browserTime(state, call.answeredAt);
  const endedAt = browserTime(state, call.endedAt);
  // A dropped stream stops the clock: it could no longer tell that the call ended.
  const elapsed = useElapsed(!ended && call.phase === "talking" && state.live ? answeredAt : null);
  const duration = ended && answeredAt !== null && endedAt !== null ? Math.max(0, (endedAt - answeredAt) / 1000) : null;
  const name = call.companyName || call.domain || t("aiCalls.live.noCompany");
  const takeoverLine = takeoverState(call);

  return (
    <>
      <header className="crm-ailive-head">
        <span className="crm-ailive-dot" aria-hidden="true" />
        <strong className="crm-ailive-title">{t(ended ? "aiCalls.live.titleEnded" : "aiCalls.live.title")}</strong>
        {call.test ? <StatusPill tone="info">{t("aiCalls.test")}</StatusPill> : null}
        {others > 0 ? <span className="crm-sub text-xs">{t("aiCalls.live.more", { n: others })}</span> : null}
        <span className="ml-auto flex items-center gap-1">
          <IconBtn
            small
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={t(open ? "aiCalls.live.collapse" : "aiCalls.live.expand")}
            title={t(open ? "aiCalls.live.collapse" : "aiCalls.live.expand")}
          >
            {open ? <ChevronDown className="size-3.5" /> : <ChevronUp className="size-3.5" />}
          </IconBtn>
          <IconBtn small onClick={onDismiss} aria-label={t("aiCalls.live.dismiss")} title={t("aiCalls.live.dismiss")}>
            <X className="size-3.5" />
          </IconBtn>
        </span>
      </header>

      <div className="crm-ailive-who">
        {call.regCode ? (
          <Link to={`/ariregister/${call.regCode}`} className="crm-ailive-company">
            {name}
          </Link>
        ) : (
          <span className="crm-ailive-company">{name}</span>
        )}
        <AiCallDialing number={call.toNumber} phase={call.phase} />
        {call.test && call.intendedNumber ? (
          <span className="crm-sub text-xs">{t("aiCalls.live.realNumber", { real: formatPhone(call.intendedNumber) })}</span>
        ) : null}
      </div>

      <div className="crm-ailive-state">
        {/* Only the words are announced: a live region around the clock would read it out every second. */}
        <span role="status" aria-live="polite">
          {ended ? t(`aiCalls.live.end.${endReason(call)}`) : t(`aiCalls.live.phase.${call.phase}`)}
        </span>
        {call.phase === "talking" && state.live ? <span className="tabular-nums">{fmtDuration(elapsed)}</span> : null}
        {duration !== null ? <span className="tabular-nums">{fmtDuration(duration)}</span> : null}
        {ended && call.disposition ? (
          <StatusPill tone={dispositionTone(call.disposition)}>
            {t(`aiCalls.disposition.${dispositionKey(call.disposition)}`)}
          </StatusPill>
        ) : null}
      </div>
      {takeoverLine ? (
        <p className="crm-ailive-quiet" role="status">
          {t(`aiCalls.live.takeover.state.${takeoverLine}`)}
        </p>
      ) : null}
      {/* The person's half of a taken-over call. Said in words once stopped
          rather than by the dot simply going out: an absent dot is also what an
          unrecorded call looks like, and the operator has just promised
          somebody that something was DONE. */}
      {!ended && call.personRecording === "recording" ? (
        <p className="crm-ailive-quiet flex items-center gap-1.5">
          <Circle className="size-2 animate-pulse fill-red-500 text-red-500" aria-hidden="true" />
          {t("aiCalls.live.recording.on")}
        </p>
      ) : null}
      {!ended && call.personRecording === "stopped" ? (
        <p className="crm-ailive-quiet" role="status">
          {t("aiCalls.live.recording.stopped")}
        </p>
      ) : null}
      {!ended && !state.live ? (
        <p className="crm-ailive-quiet" role="status">
          {t("aiCalls.live.reconnecting")}
        </p>
      ) : null}

      {open ? (
        <div className="crm-ailive-body">
          {call.campaignName ? <p className="crm-sub text-xs">{t("aiCalls.live.campaign", { name: call.campaignName })}</p> : null}
          {call.recordObjected ? (
            <Note tone="warn">{t("aiCalls.live.recordObjected")}</Note>
          ) : call.phase === "talking" || call.turns.length > 0 ? (
            <AiCallTranscript turns={call.turns} />
          ) : null}
          {ended && call.summary ? <p className="crm-ailive-summary">{call.summary}</p> : null}
        </div>
      ) : null}
    </>
  );
}

/** "End call": one press, no question. What went wrong, if anything, under it. */
function HangupButton({ callId, canManage }: { callId: string; canManage: boolean }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<HangupError | null>(null);

  async function press() {
    setBusy(true);
    setError(null);
    try {
      await hangupAiCall(callId);
    } catch (err) {
      setError(hangupError(err));
    }
    setBusy(false);
  }

  return (
    <>
      <Btn tone="danger" small onClick={() => void press()} disabled={busy}>
        {busy ? <Loader2 className="size-3.5 animate-spin" /> : <PhoneOff className="size-3.5" />}
        {t("aiCalls.live.hangup")}
      </Btn>
      {error ? (
        <Note tone="bad" live className="crm-ailive-error">
          {t(`aiCalls.live.hangupError.${error}`)}
          {/* Only a manager has the button the hint names. */}
          {error === "hangup_failed" && canManage
            ? ` ${t("aiCalls.live.hangupStopHint", { stop: t("aiCalls.automation.controls.stop") })}`
            : null}
        </Note>
      ) : null}
    </>
  );
}
